import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardList,
  Clock,
  FolderPlus,
  LogOut,
  Moon,
  PenLine,
  Plus,
  Send,
  Sun,
  UserPlus,
  Zap,
} from 'lucide-react';
import { instantGlobalSearch } from '../api/client';
import TimerButton from '../components/TimerButton';
import CommandPalette from '../components/CommandPalette';
import { useDebounce } from '../hooks/useDebounce';
import { useRecentItems } from '../hooks/useRecentItems';
import { useThemePreference } from '../hooks/useThemePreference';
import type { TopBarAction } from './TopBarActionsContext';
import { CreateMenu, type CreateMenuItem } from './top-bar/CreateMenu';
import { RecentMattersMenu } from './top-bar/RecentMattersMenu';
import { TopBarSearchBox } from './top-bar/TopBarSearchBox';
import { useTopBarShortcuts } from './top-bar/useTopBarShortcuts';

interface AppTopBarUser {
  name?: string | null;
  username?: string | null;
}

interface AppTopBarProps {
  authEnabled: boolean;
  isMobile: boolean;
  logout: () => void;
  onCloseMoreMenu: () => void;
  onCreateContact: () => void;
  onCreateMatter: () => void;
  onPunchClock: () => void;
  onQuickIntake: () => void;
  topBarActions: TopBarAction[];
  user: AppTopBarUser | null;
}

export default function AppTopBar({
  authEnabled,
  isMobile,
  logout,
  onCloseMoreMenu,
  onCreateContact,
  onCreateMatter,
  onPunchClock,
  onQuickIntake,
  topBarActions,
  user,
}: AppTopBarProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [semanticMode, setSemanticMode] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showRecentMenu, setShowRecentMenu] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLDivElement>(null);
  const recentRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedTerm = useDebounce(searchTerm, 300);
  const recentItems = useRecentItems();
  const { preference: themePreference, resolvedTheme, toggleTheme } = useThemePreference();
  const ThemeIcon = resolvedTheme === 'dark' ? Sun : Moon;
  const themeToggleLabel = resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';

  const { data: results } = useQuery({
    queryKey: ['search', debouncedTerm, semanticMode],
    queryFn: () => instantGlobalSearch(debouncedTerm),
    enabled: debouncedTerm.length >= 2,
  });

  useTopBarShortcuts({
    navigate,
    onCloseMoreMenu,
    searchInputRef,
    setShowCommandPalette,
    setShowCreateMenu,
    setShowRecentMenu,
    setShowResults,
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
      if (createRef.current && !createRef.current.contains(e.target as Node)) {
        setShowCreateMenu(false);
      }
      if (recentRef.current && !recentRef.current.contains(e.target as Node)) {
        setShowRecentMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (path: string) => {
    navigate(path);
    setSearchTerm('');
    setShowResults(false);
  };

  const handleFullSearch = () => {
    setShowResults(false);
    navigate(`/search?q=${encodeURIComponent(debouncedTerm.trim())}`);
  };

  const handleSubmitSearch = () => {
    setShowResults(false);
    navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
  };

  const handleCreateAction = (action: () => void) => {
    setShowCreateMenu(false);
    action();
  };

  const createMenuItems: CreateMenuItem[] = [
    { label: 'New Matter', desc: 'Create a case or intake', icon: FolderPlus, bg: 'var(--primary-light)', color: 'var(--primary)', action: onCreateMatter },
    { label: 'Quick Intake', desc: 'Contact + Matter + Form', icon: Zap, bg: 'var(--warning-light)', color: '#f59e0b', action: onQuickIntake },
    { label: 'Full Intake', desc: 'Comprehensive intake form', icon: ClipboardList, bg: '#fff1f2', color: '#e11d48', action: () => navigate('/intake') },
    { label: 'New Contact', desc: 'Add a person or lead', icon: UserPlus, bg: 'var(--success-light)', color: '#059669', action: onCreateContact },
    { label: 'Send Document', desc: 'E-sign or send a template', icon: Send, bg: 'var(--warning-light)', color: '#d97706', action: () => navigate('/documents') },
    { label: 'Fill Form', desc: 'Open a form on a matter', icon: PenLine, bg: 'var(--info-light)', color: '#2563eb', action: () => navigate('/matters') },
    { label: 'Punch Clock', desc: 'Punch in or out', icon: Clock, bg: '#f0fdf4', color: '#16a34a', action: onPunchClock },
  ];

  return (
    <>
      <header
        style={{
          height: 56,
          background: 'var(--card)',
          borderBottom: '1px solid var(--border)',
          borderImage: 'linear-gradient(90deg, #f43f5e, #f59e0b) 1',
          borderImageSlice: 1,
          borderImageWidth: '0 0 2px 0',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '0 0.75rem' : '0 1.25rem',
          gap: '0.75rem',
          flexShrink: 0,
          boxShadow: 'var(--shadow-sm)',
          position: 'relative',
          zIndex: 40,
        }}
      >
        <TopBarSearchBox
          debouncedTerm={debouncedTerm}
          isMobile={isMobile}
          results={results}
          searchInputRef={searchInputRef}
          searchRef={searchRef}
          searchTerm={searchTerm}
          semanticMode={semanticMode}
          showResults={showResults}
          onCommandPaletteOpen={() => setShowCommandPalette(true)}
          onFullSearch={handleFullSearch}
          onSearchTermChange={setSearchTerm}
          onSelect={handleSelect}
          onSemanticModeChange={setSemanticMode}
          onShowResultsChange={setShowResults}
          onSubmitSearch={handleSubmitSearch}
        />
        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto', alignItems: 'center' }}>
          {!isMobile && topBarActions.map((action, i) => (
            <button
              key={i}
              onClick={action.onClick}
              style={{
                padding: '0.4rem 0.9rem',
                background: action.primary ? 'var(--primary)' : 'var(--card)',
                color: action.primary ? '#fff' : 'var(--text)',
                border: action.primary ? 'none' : '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (action.primary) e.currentTarget.style.background = 'var(--primary-hover)';
                else e.currentTarget.style.background = 'var(--bg)';
              }}
              onMouseLeave={(e) => {
                if (action.primary) e.currentTarget.style.background = 'var(--primary)';
                else e.currentTarget.style.background = 'var(--card)';
              }}
            >
              {action.label}
            </button>
          ))}

          <TimerButton />

          <button
            type="button"
            aria-label={themeToggleLabel}
            title={themePreference === 'system' ? `${themeToggleLabel} (system theme)` : themeToggleLabel}
            onClick={toggleTheme}
            style={{
              width: isMobile ? 44 : 36,
              minWidth: isMobile ? 44 : 36,
              height: isMobile ? 44 : 36,
              minHeight: isMobile ? 44 : 36,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--card)',
              color: 'var(--text-secondary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-hover)';
              e.currentTarget.style.color = 'var(--primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--card)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            <ThemeIcon size={18} />
          </button>

          {!isMobile && (
            <div ref={recentRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button
                onClick={() => setShowRecentMenu(!showRecentMenu)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.35rem',
                  borderRadius: 'var(--radius-sm)',
                  color: showRecentMenu ? 'var(--primary)' : 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.background = 'var(--bg)'; }}
                onMouseLeave={(e) => { if (!showRecentMenu) { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; } }}
                title="Recent matters"
              >
                <Clock size={18} />
              </button>
              {showRecentMenu && (
                <RecentMattersMenu
                  items={recentItems.items}
                  onClearAll={recentItems.clearAll}
                  onSelect={(item) => {
                    navigate(`/matters/${item.id}`);
                    setShowRecentMenu(false);
                  }}
                />
              )}
            </div>
          )}

          {!isMobile && (
            <div ref={createRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button
                aria-label="Create new"
                onClick={() => setShowCreateMenu(!showCreateMenu)}
                style={{
                  width: 36,
                  minWidth: 36,
                  height: 36,
                  minHeight: 36,
                  borderRadius: '50%',
                  background: 'var(--gradient-primary)',
                  color: '#fff',
                  border: 'none',
                  padding: 0,
                  fontSize: '1.25rem',
                  fontWeight: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  lineHeight: 1,
                  boxShadow: '0 2px 8px var(--primary-glow)',
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.08)';
                  e.currentTarget.style.boxShadow = '0 4px 16px var(--primary-glow)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px var(--primary-glow)';
                }}
                title="Create new..."
              >
                <Plus size={20} />
              </button>
              {showCreateMenu && (
                <CreateMenu items={createMenuItems} onSelect={handleCreateAction} />
              )}
            </div>
          )}

          {!isMobile && authEnabled && user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem', paddingLeft: '0.75rem', borderLeft: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {user.name || user.username || ''}
              </span>
              <button
                onClick={logout}
                title="Sign out"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.3rem',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
              >
                <LogOut size={16} />
              </button>
            </div>
          )}

          {isMobile && authEnabled && user && (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1a1023, #2d1f3d)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
              title={user.name || user.username || ''}
            >
              {(user.name || user.username || '?').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </header>

      {showCommandPalette && (
        <CommandPalette
          onClose={() => setShowCommandPalette(false)}
          recentItems={recentItems.items}
          onCreateMatter={() => { setShowCommandPalette(false); onCreateMatter(); }}
          onCreateContact={() => { setShowCommandPalette(false); onCreateContact(); }}
          onQuickIntake={() => { setShowCommandPalette(false); onQuickIntake(); }}
        />
      )}
    </>
  );
}
